import * as React from 'react'
import { FunctionComponent, ReactElement, useContext } from 'react'
import SessionCardMobileButton from '@builder/react-ui/scheduler/session-card-mobile/session-card-mobile-button'
import {
  IoAddCircleOutline as IncludeSessionIcon,
  IoBanSharp as SkipSessionIcon,
  IoPlayCircle as PlayIcon,
  IoLink as LinkIcon
} from 'react-icons/io5'

import { FaCheck as CompletedIcon } from 'react-icons/fa'
import { HiDownload as DownloadIcon } from 'react-icons/hi'
import { SaveScheduleContext } from '@react-ui/context'

export interface SessionCardMobileProps {
  qaId: string
  thumbnail: {
    url: string
  }
  mediaType: 'link' | 'document' | 'video'
  title: string
  dayLabel: string
  clickHandler: () => void
  toggleSkip: () => void
  date: string
  isCompleted: boolean
  isSkipped: boolean
  isCurrent: boolean
  calendar: ReactElement
  heading: string
}

const SessionCardMobile: FunctionComponent<SessionCardMobileProps> = ({
  qaId,
  thumbnail,
  title,
  dayLabel,
  clickHandler,
  toggleSkip,
  date,
  isCompleted = false,
  isCurrent = false,
  isSkipped = false,
  heading,
  calendar,
  mediaType
}) => {
  const { userCanModify } = useContext(SaveScheduleContext)

  return (
    <div
      data-qa-name={qaId}
      className={`font-body flex flex-col justify-stretch bg-alpha-white border border-solid cursor-auto w-full rounded-lg overflow-hidden 
      ${isCurrent ? 'border-alpha-oatmeal-20 shadow-lg' : 'border-gray-200 shadow-sm'}`}
    >
      <p
        className={`flex items-center gap-1 m-0 py-2 px-4 text-alpha-white font-bold text-xs leading-none
 uppercase ${isSkipped ? 'opacity-60' : ''}`}
        style={{
          background: isCompleted ? '#00BBA5' : '#000'
        }}
      >
        {isCompleted ? (
          <CompletedIcon data-qa-name='session-completed-icon' />
        ) : null}
        {isSkipped ? <SkipSessionIcon /> : null}
        <span>{heading}</span>
      </p>
      {thumbnail?.url && !isSkipped ? (
        <div
          data-qa-name='session-thumbnail'
          onClick={clickHandler}
          className={`aspect-video bg-cover bg-center flex justify-center items-center text-alpha-grey-light' ${isSkipped ? 'opacity-60' : ''}`}
          style={{
            backgroundImage: `url(${thumbnail.url})`
          }}
        >
          {mediaType === 'video' ? (
            <PlayIcon
              data-qa-name='play-icon'
              color='white'
              size='8rem'
              className='opacity-75'
            />
          ) : null}
          {mediaType === 'document' ? (
            <DownloadIcon
              data-qa-name='download-icon'
              color='white'
              size='8rem'
              className='opacity-75'
            />
          ) : null}
          {mediaType === 'link' ? (
            <LinkIcon
              data-qa-name='link-icon'
              color='white'
              size='8rem'
              className='opacity-75'
            />
          ) : null}
        </div>
      ) : null}

      <div className='flex flex-col p-4 gap-3'>
        <div className='flex flex-col text-base' onClick={clickHandler}>
          <b data-qa-name='session-title' className='m-0'>
            {title}
          </b>
          <p className='m-0'>
            <span data-qa-name='day-label'>{`${dayLabel} `}</span>
            <span data-qa-name='date-label'>{date}</span>
          </p>
        </div>
        {isSkipped ? (
          <SessionCardMobileButton
            data-qa-name={'undoSkipButton'}
            action={toggleSkip}
            icon={<IncludeSessionIcon />}
            text={$localize`:schedule.button|Schedule - Undo Skip Session Button@@scheduleUndoSkipButton:Undo Skip`}
          />
        ) : null}
        {userCanModify && !isSkipped && !isCompleted ? (
          <>
            {calendar}
            <SessionCardMobileButton
              data-qa-name={'skipButton'}
              action={toggleSkip}
              icon={<SkipSessionIcon />}
              text={$localize`:schedule.button|Schedule - Skip Session Button@@scheduleSkipSessionButton:Skip Session`}
            />
          </>
        ) : null}
      </div>
    </div>
  )
}

export default SessionCardMobile
